/**
 * The sky behind the terrain (GDD previews): a gradient, the sun or the Earth, stars, clouds and two ranges of
 * distant mountains. Each layer is drawn once at one pixel per cell, then scaled like the terrain, so it keeps
 * the pixel look; the far layers scroll slower (horizontal parallax). Display only.
 */
import type { Camera } from './camera';
import type { Theme } from './themes';

interface Layer { canvas: HTMLCanvasElement; parallax: number }

/** Small seeded generator (display only: the same sky every time for a map). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class Backdrop {
  private readonly layers: Layer[] = [];

  constructor(theme: Theme, private readonly w: number, private readonly h: number) {
    const sky = this.layer(0.1);
    const g = sky.getContext('2d')!;
    const grad = g.createLinearGradient(0, 0, 0, h);
    for (const [at, color] of theme.sky) grad.addColorStop(at, color);
    g.fillStyle = grad;
    g.fillRect(0, 0, w, h);
    if (theme.stars) {
      const r = rng(77);
      for (let i = 0; i < theme.stars; i++) {
        const bright = r() < 0.15;
        g.fillStyle = bright ? '#e8ecf8' : `rgba(200, 205, 220, ${0.35 + r() * 0.4})`;
        g.fillRect(Math.floor(r() * w), Math.floor(r() * h * 0.8), 1, 1);
      }
    }
    if (theme.sun) {
      const s = theme.sun;
      this.disc(g, s.x, s.y, Math.round(s.r * 1.7), s.glow);
      this.disc(g, s.x, s.y, s.r, s.color);
    }
    if (theme.earth) this.earth(g, theme.earth.x, theme.earth.y, theme.earth.r);

    if (theme.clouds) {
      const c = theme.clouds;
      const cl = this.layer(0.2).getContext('2d')!;
      const r = rng(1234);
      for (let i = 0; i < c.count; i++) {
        const x = Math.floor(r() * w);
        const y = Math.floor(c.top + r() * (c.bottom - c.top));
        const rx = 40 + Math.floor(r() * 70);
        const ry = 5 + Math.floor(r() * 6);
        // Flat clouds, a lighter core over a darker rim (and wrapped around the map's sides).
        for (const ox of [0, -w, w]) {
          this.ellipse(cl, x + ox, y, rx, ry, c.color);
          this.ellipse(cl, x + ox + Math.floor(rx / 6), y - 1, Math.floor(rx * 0.7), ry - 2, c.light);
        }
      }
    }

    for (const m of theme.mountains) {
      const mc = this.layer(m.parallax).getContext('2d')!;
      const r = rng(m.seed * 9973);
      // Broad, gentle ranges (GDD previews): one wave of `peaks` across the map, two small ones over it, all
      // together `height` cells above and below the ridge.
      const weights = [1, 0.35, 0.12];
      const total = weights.reduce((t, v) => t + v, 0);
      const waves = weights.map((wt, k) => ({ f: (m.peaks * [1, 2.3, 5.1][k] * (0.8 + 0.4 * r())) / w, p: r() * Math.PI * 2, a: (m.height * wt) / total }));
      mc.fillStyle = m.color;
      for (let x = 0; x < w; x++) {
        let y = m.ridge;
        for (const wv of waves) y -= wv.a * Math.sin(x * wv.f * Math.PI * 2 + wv.p);
        mc.fillRect(x, Math.round(y), 1, h - Math.round(y));
      }
    }
  }

  private layer(parallax: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = this.w;
    canvas.height = this.h;
    this.layers.push({ canvas, parallax });
    return canvas;
  }

  /** A filled disc, pixel by pixel (no anti-aliasing: it is scaled up like the terrain). */
  private disc(g: CanvasRenderingContext2D, cx: number, cy: number, r: number, color: string): void {
    this.ellipse(g, cx, cy, r, r, color);
  }

  private ellipse(g: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number, color: string): void {
    g.fillStyle = color;
    for (let dy = -ry; dy <= ry; dy++) {
      const half = Math.floor(rx * Math.sqrt(1 - (dy * dy) / (ry * ry)));
      g.fillRect(cx - half, cy + dy, 2 * half + 1, 1);
    }
  }

  /** The Earth: blue, green lands, the night side in shadow. */
  private earth(g: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
    this.disc(g, cx, cy, r, '#2f6fd0');
    const lands: [number, number, number, number][] = [[-12, -18, 9, 6], [6, -2, 12, 8], [-16, 12, 8, 7], [14, 20, 7, 5], [-2, -30, 6, 3]];
    for (const [dx, dy, rx, ry] of lands) {
      g.fillStyle = '#4c9e44';
      for (let y = -ry; y <= ry; y++) {
        const half = Math.floor(rx * Math.sqrt(1 - (y * y) / (ry * ry)));
        for (let x = -half; x <= half; x++) {
          if ((dx + x) ** 2 + (dy + y) ** 2 <= (r - 1) ** 2) g.fillRect(cx + dx + x, cy + dy + y, 1, 1);
        }
      }
    }
    // The night side: everything left of a curve through the disc.
    g.fillStyle = 'rgba(8, 14, 40, 0.72)';
    for (let y = -r; y <= r; y++) {
      const half = Math.floor(Math.sqrt(r * r - y * y));
      const edge = Math.floor(-half * 0.35);
      g.fillRect(cx - half, cy + y, edge + half, 1);
    }
  }

  draw(ctx: CanvasRenderingContext2D, cam: Camera): void {
    const s = cam.scale;
    ctx.imageSmoothingEnabled = false;
    for (const l of this.layers) {
      const lx = cam.x * l.parallax;
      const sx0 = Math.max(0, lx);
      const sy0 = Math.max(0, cam.y);
      const sx1 = Math.min(this.w, lx + cam.spanW);
      const sy1 = Math.min(this.h, cam.y + cam.spanH);
      if (sx1 <= sx0 || sy1 <= sy0) continue;
      ctx.drawImage(l.canvas, sx0, sy0, sx1 - sx0, sy1 - sy0, (sx0 - lx) * s, (sy0 - cam.y) * s, (sx1 - sx0) * s, (sy1 - sy0) * s);
    }
  }
}
